import { __assign } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState, } from "react";
import isEmpty from "lodash/isEmpty";
import pickBy from "lodash/pickBy";
import find from "lodash/find";
import uniq from "lodash/uniq";
import keyBy from "lodash/keyBy";
import { LinedPangeaDataGrid, useGridSchemaColumns, } from "@pangeacyber/react-mui-shared";
import { ObjectStore } from "../../types";
import { StoreViewerFields, StoreViewerFieldsWithID, StoreViewerFilters, } from "./fields";
import { useStoreFileViewerContext, useStoreFileViewerFolder, } from "../../hooks/context";
import FolderPath from "./FolderPath";
import PreviewStoreFile from "../PreviewStoreFile";
import CreateNewButton from "../CreateNewButton";
import { Stack, Chip } from "@mui/material";
import FileOptions from "../FileOptions";
import { PREVIEW_FILE_WIDTH } from "../PreviewStoreFile/constants";
import MultiSelectMenu from "./MultiSelectMenu";
import UploadPopover from "../UploadPopover";
import DataGridParentStack from "./DataGridParentStack";
import { downloadFile } from "../../utils/file";
import DownloadPopover from "../DownloadPasswordPopover";
import BucketSelector from "./BucketSelector";
export var DEFAULT_VISIBILITY_MODEL = {
    name: true,
    size: true,
    updated_at: true,
};
export var DEFAULT_COLUMN_ORDER = ["name", "size", "updated_at"];
var StoreDataGrid = function (_a) {
    var _b, _c, _d, _e;
    var defaultVisibilityModel = _a.defaultVisibilityModel, defaultColumnOrder = _a.defaultColumnOrder, customizations = _a.customizations, includeIdColumn = _a.includeIdColumn, virtualRoot = _a.virtualRoot;
    var _f = useStoreFileViewerContext(), data = _f.data, request = _f.request, reload = _f.reload, loading = _f.loading, previewId = _f.previewId, setPreviewId = _f.setPreviewId, apiRef = _f.apiRef;
    var setParentId = useStoreFileViewerFolder().setParentId;
    var filters = request.filters, setFilters = request.setFilters, page = request.page, setPage = request.setPage, pageSize = request.pageSize, setPageSize = request.setPageSize, lastKnownPage = request.lastKnownPage, sorting = request.sorting, setSorting = request.setSorting;
    var columns_ = useGridSchemaColumns(includeIdColumn ? StoreViewerFieldsWithID : StoreViewerFields);
    var columns = useMemo(function () {
        return columns_.map(function (column_) {
            var _a;
            var override = (_a = customizations === null || customizations === void 0 ? void 0 : customizations.columnOverrides) === null || _a === void 0 ? void 0 : _a[column_.field];
            if (!override)
                return column_;
            return __assign(__assign({}, column_), override);
        });
    }, [columns_, customizations]);
    var _g = useState(null), contextMenu = _g[0], setContextMenu = _g[1];
    var _h = useState([]), selected = _h[0], setSelected = _h[1];
    var setMultiSelected = useCallback(function (update) {
        setSelected(function (state) {
            var _a;
            var newState = state;
            if (typeof update === "function") {
                newState = update(state);
            }
            else {
                newState = update;
            }
            var objectMap = keyBy((_a = data.objects) !== null && _a !== void 0 ? _a : [], "id");
            return uniq(newState).filter(function (objectId) { return !!objectMap[objectId]; });
        });
    }, [data, setSelected]);
    var multiSelected = useMemo(function () {
        var _a;
        var objectMap = keyBy((_a = data.objects) !== null && _a !== void 0 ? _a : [], "id");
        return selected.filter(function (objectId) { return !!objectMap[objectId]; });
    }, [selected, data]);
    var handleGetRowClassName = function (params) {
        var selected = find(multiSelected, function (id) { return id === params.row.id; });
        if (selected) {
            return "Mui-selected";
        }
        return "";
    };
    var handleContextMenu = function (event) {
        if (!multiSelected)
            return;
        var foundParent = false;
        var rowEl = event.currentTarget.closest(".MuiDataGrid-row");
        if (rowEl) {
            var row = Number(rowEl.getAttribute("data-rowindex"));
            if (!Number.isNaN(row) && row >= 0 && row < data.objects.length) {
                var object_1 = data.objects[row];
                if (event.shiftKey || multiSelected.includes(object_1.id)) {
                    setMultiSelected(function (state) { return state.concat([object_1.id]); });
                }
                else {
                    setPreviewId(object_1.id);
                    setMultiSelected([object_1.id]);
                }
                foundParent = true;
            }
        }
        if (!foundParent && !multiSelected.length)
            return;
        event.preventDefault();
        setContextMenu(contextMenu === null
            ? {
                mouseX: event.clientX + 2,
                mouseY: event.clientY - 6,
            }
            : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                // Other native context menus might behave different.
                // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                null);
    };
    var _j = useState(false), downloading = _j[0], setDownloading = _j[1];
    var handleDownloadFile = function (id) {
        var _a;
        if (downloading || !id)
            return;
        var objectMap = keyBy((_a = data.objects) !== null && _a !== void 0 ? _a : [], "id");
        var object = objectMap[id];
        if (!object)
            return;
        setDownloading(true);
        return downloadFile(object, apiRef)
            .then(function () {
            setDownloading(false);
        })
            .catch(function (err) {
            setDownloading(false);
        });
    };
    var isFiltered = !isEmpty(pickBy(filters !== null && filters !== void 0 ? filters : {}, function (v, k) { return !!v && k !== "parent_id"; }));
    var visibilityModel = useMemo(function () {
        return (defaultVisibilityModel !== null && defaultVisibilityModel !== void 0 ? defaultVisibilityModel : __assign(__assign({}, DEFAULT_VISIBILITY_MODEL), { parent_id: isFiltered ? true : false }));
    }, [defaultVisibilityModel, columns, isFiltered]);
    var displayPreviewId = useMemo(function () {
        if (multiSelected.length > 1 || multiSelected.length === 0) {
            return null;
        }
        return (filters === null || filters === void 0 ? void 0 : filters.id) || previewId;
    }, [filters, previewId, multiSelected]);
    var rowCount = (_d = (_b = data === null || data === void 0 ? void 0 : data.count) !== null && _b !== void 0 ? _b : (_c = data === null || data === void 0 ? void 0 : data.objects) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0;
    return (_jsxs(Stack, { spacing: 1, sx: {
            ".PangeaDataGrid-HeaderStack-root": {
                padding: "16px 16px 0 16px",
            },
        }, children: [_jsx(LinedPangeaDataGrid, { columns: columns, data: data.objects, PreviewPanel: {
                    component: PreviewStoreFile,
                    width: "".concat(PREVIEW_FILE_WIDTH, "px"),
                    position: "fullHeight",
                }, previewId: displayPreviewId, onRowDoubleClick: function (params) {
                    if (params.row.type === ObjectStore.ObjectType.Folder) {
                        setParentId(params.row.id);
                        return false;
                    }
                    else {
                        handleDownloadFile(params.row.id);
                    }
                }, onRowClick: function (params, event) {
                    var _a;
                    if (event.shiftKey) {
                        event.preventDefault();
                        (_a = document.getSelection()) === null || _a === void 0 ? void 0 : _a.removeAllRanges();
                        setMultiSelected(function (state) { return state.concat([params.row.id]); });
                    }
                    else {
                        setPreviewId(params.row.id);
                        setMultiSelected([params.row.id]);
                    }
                    return false;
                }, onPreview: function (preview) {
                    if (!preview && multiSelected.length === 1)
                        setMultiSelected([]);
                }, DataGridProps: __assign({}, (!!apiRef.getArchive && {
                    getRowClassName: handleGetRowClassName,
                    slotProps: {
                        row: {
                            onContextMenu: handleContextMenu,
                            style: { cursor: "context-menu" },
                        },
                    },
                })), ColumnCustomization: {
                    visibilityModel: visibilityModel,
                    order: defaultColumnOrder !== null && defaultColumnOrder !== void 0 ? defaultColumnOrder : DEFAULT_COLUMN_ORDER,
                    position: "inline",
                    dynamicFlexColumn: true,
                }, ActionColumn: {
                    render: function (object) { return (_jsx(FileOptions, { object: object, onClose: function () { }, displayDownloadInline: true })); },
                    isPinned: true,
                    GridColDef: {
                        minWidth: 100,
                    },
                }, loading: loading, header: _jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", width: "100%", children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { paddingBottom: 1, marginLeft: -0.5 }, children: [_jsx(BucketSelector, {}), _jsx(FolderPath, {})] }), multiSelected.length > 1 && (_jsx(Chip, { size: "small", label: "".concat(multiSelected.length, " Selected"), onDelete: function () {
                                setMultiSelected([]);
                            } }))] }), Search: {
                    query: (_e = filters === null || filters === void 0 ? void 0 : filters.name) !== null && _e !== void 0 ? _e : "",
                    placeholder: "Search by name...",
                    onChange: function (query) {
                        // @ts-ignore
                        if (query !== (filters === null || filters === void 0 ? void 0 : filters.name)) {
                            setFilters(function (state) { return (__assign(__assign({}, state), { name__contains: query })); });
                        }
                        else {
                            reload();
                        }
                    },
                    Filters: {
                        filters: filters,
                        onFilterChange: setFilters,
                        // @ts-ignore
                        options: StoreViewerFilters,
                        showFilterChips: true,
                    },
                    EndBarComponent: _jsx(CreateNewButton, {}),
                    SearchButtonProps: {
                        color: "primary",
                    },
                }, ServerPagination: {
                    page: page,
                    onPageChange: function (page) { return setPage(page); },
                    pageSize: pageSize,
                    onPageSizeChange: setPageSize,
                    rowsPerPageOptions: [5, 10, 20, 50],
                    paginationRowCount: Math.min(lastKnownPage * pageSize, rowCount),
                    rowCount: rowCount,
                }, ServerSorting: {
                    // @ts-ignore "asc" | "desc" | undefined
                    sort: sorting.order,
                    sortBy: sorting.order_by,
                    onSortChange: function (sort, sortBy) {
                        setSorting({
                            order: sort,
                            order_by: sortBy,
                        });
                    },
                }, components: {
                    DataGridParentStack: DataGridParentStack,
                } }), _jsx(UploadPopover, {}), _jsx(DownloadPopover, {}), _jsx(MultiSelectMenu, { selected: multiSelected, contextMenu: contextMenu, setContextMenu: setContextMenu })] }));
};
export default StoreDataGrid;
